import React from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export interface IndexCategoryOption<T extends string> {
  value: T;
  label: string;
}

/** Full-width row inside the index grid, regardless of grid/flex layout. */
const fullWidthSx = { width: '100%', gridColumn: '1 / -1' };

export const IndexSectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Typography variant="h5" sx={{ ...fullWidthSx, fontWeight: 700, mt: 1 }}>{label}</Typography>
);

export const IndexGroupHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={fullWidthSx}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{label}</Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  </Box>
);

interface IndexLayoutProps<T extends string> {
  query: string;
  onQueryChange: (query: string) => void;
  searchPlaceholder: string;
  categories: IndexCategoryOption<T>[];
  category: T;
  onCategoryChange: (category: T) => void;
  loading?: boolean;
  isEmpty: boolean;
  emptyMessage: string;
  headerSx?: object;
  contentSx?: object;
  gridSx?: object;
  children: React.ReactNode;
}

const defaultGridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 };

export function IndexLayout<T extends string>({
  query,
  onQueryChange,
  searchPlaceholder,
  categories,
  category,
  onCategoryChange,
  loading = false,
  isEmpty,
  emptyMessage,
  headerSx,
  contentSx,
  gridSx,
  children,
}: IndexLayoutProps<T>) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...headerSx }}>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            },
          }}
          fullWidth
        />
        <Tabs
          value={category}
          onChange={(_, value) => onCategoryChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Index categories"
        >
          {categories.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
        </Tabs>
      </Box>
      <Box sx={contentSx}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : isEmpty ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {emptyMessage}
          </Typography>
        ) : (
          <Box sx={gridSx ?? defaultGridSx}>{children}</Box>
        )}
      </Box>
    </Box>
  );
}
